/**
 * src/shell/ui/screens/DecisionScreen.tsx — S3 文件堆 · 决策屏（设计稿 §1.2 S3 / §2 Phase 3）。
 *
 * 待拍板列表 → 决策详情（三选项卡：style 徽章 + expected_impact）→ 「就这么定」。
 * 全部拍板后「开始开发」（端口写回 + enqueue，引擎 §8.3）。
 * 第三节「待上架」（缺陷 C 修复，2026-08-15 拍板）：in_review 项目直达电脑详情。
 */

import { useState } from 'react'
import type { Decision } from '../../../gm'

/** 选项键（gm index 未单独导出 OptionKey——从 Decision 派生） */
type OptionKey = Decision['options'][number]['key']
import { COPY } from '../../copy'
import { chooseOption, confirmDevelopment } from '../../gm-client/actions'
import { useSave } from '../../gm-client/hooks'
import { DESK, type Screen } from '../nav'

const C = COPY.decisions

export function DecisionScreen({ decisionId, go }: { decisionId?: string; go: (s: Screen) => void }) {
  const { bundle } = useSave()
  const pending = bundle.decisions.decisions.filter((d) => d.status === 'pending')
  const current = decisionId
    ? bundle.decisions.decisions.find((d) => d.id === decisionId)
    : undefined

  // 全部拍板完毕的项目（等玩家点「开始开发」）
  const readyProjects = bundle.projects.projects.filter(
    (p) =>
      p.status === 'awaiting_decision' &&
      p.decision_ids.length > 0 &&
      p.decision_ids.every(
        (id) => bundle.decisions.decisions.find((d) => d.id === id)?.status === 'resolved',
      ),
  )

  // 待上架（缺陷 C 修复，2026-08-15 拍板）：验收通过待玩家点「提交上架」的项目
  const inReview = bundle.projects.projects.filter((p) => p.status === 'in_review')

  return (
    <div className="screen deep-screen">
      <button className="btn back-btn" type="button" onClick={() => go(DESK)}>
        {COPY.nav.backToDesk}
      </button>
      <h3>{C.listTitle}</h3>

      {current ? (
        <DecisionDetail decision={current} go={go} />
      ) : (
        <div className="panel">
          {pending.length === 0 && readyProjects.length === 0 && inReview.length === 0 && (
            <p className="dim">{C.empty}</p>
          )}
          {pending.map((d) => (
            <button
              key={d.id}
              type="button"
              className="list-row"
              onClick={() => go({ name: 'decisions', decisionId: d.id })}
            >
              <span className="list-title">{d.title}</span>
              <span className="badge">{d.kind}</span>
            </button>
          ))}
          {readyProjects.map((p) => (
            <div key={p.id} className="graybox">
              <p>
                {C.allResolved} — <span className="dim mono">{p.id}</span>
              </p>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  void confirmDevelopment(p.id).then(() => go(DESK))
                }}
              >
                {COPY.computer.startDevelopment}
              </button>
            </div>
          ))}
          {/* 待上架节：点击直达电脑项目详情（上架按钮在场） */}
          {inReview.length > 0 && (
            <h4>{COPY.cockpit.pendingPublish(inReview.length)}</h4>
          )}
          {inReview.map((p) => (
            <button
              key={p.id}
              type="button"
              className="list-row"
              onClick={() => go({ name: 'computer', tab: 'projects', projectId: p.id })}
            >
              <span className="list-title">{p.idea.slice(0, 40)}</span>
              <span className="badge">{COPY.computer.status[p.status]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function DecisionDetail({ decision, go }: { decision: Decision; go: (s: Screen) => void }) {
  const { bundle } = useSave()
  const [picked, setPicked] = useState<OptionKey | null>(decision.player_choice)
  const raiser = bundle.employees.employees.find((e) => e.id === decision.raised_by)

  return (
    <div className="panel">
      <button className="btn" type="button" onClick={() => go({ name: 'decisions' })}>
        {COPY.nav.back}
      </button>
      <h3>{decision.title}</h3>
      <p className="dim">{raiser ? C.from(raiser.name) : decision.raised_by}</p>
      <p>{decision.description}</p>
      <div className="option-grid">
        {decision.options.map((o) => (
          <button
            key={o.key}
            type="button"
            className={picked === o.key ? 'option-card option-picked' : 'option-card'}
            onClick={() => setPicked(o.key)}
          >
            <span className={`badge style-${o.style}`}>{C.styleBadge[o.style]}</span>
            <span className="option-label">{o.label}</span>
            <span className="dim">{o.description}</span>
            <span className="option-impact">
              {C.impact}：{o.expected_impact}
            </span>
          </button>
        ))}
      </div>
      {decision.status === 'resolved' ? (
        <p className="badge">{C.resolved}</p>
      ) : (
        <button
          className="btn btn-primary"
          type="button"
          disabled={!picked}
          onClick={() => {
            if (!picked) return
            void chooseOption(decision, picked).then(() => go({ name: 'decisions' }))
          }}
        >
          {C.confirm}
        </button>
      )}
    </div>
  )
}
